import {
  Alert, Badge, Box, Button, Container, CopyToClipboard, ExpandableSection,
  Flashbar, Header, KeyValuePairs, SpaceBetween, Spinner, StatusIndicator, Table,
} from '@cloudscape-design/components';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  FmBundle, fmStatusType, getFmBundle, getFmBundleStatus, MissingCredsError,
} from './fmBundles';

/** AWS region where the FM bundle role, AIPs and Bedrock runtime live. */
const FM_REGION = 'eu-west-1';

/** Formats an optional USD budget value for display. */
function formatBudget(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

/** Formats the bundle's single budget as "Monthly: $X" / "Yearly: $X" (both if legacy data sets both). */
function formatBudgetPeriod(bundle: FmBundle): string {
  const hasMonthly = bundle.monthlyBudgetUSD !== null && bundle.monthlyBudgetUSD !== undefined;
  const hasYearly = bundle.yearlyBudgetUSD !== null && bundle.yearlyBudgetUSD !== undefined;
  const parts: string[] = [];
  if (hasMonthly) parts.push(`Monthly: ${formatBudget(bundle.monthlyBudgetUSD)}`);
  if (hasYearly) parts.push(`Yearly: ${formatBudget(bundle.yearlyBudgetUSD)}`);
  return parts.length ? parts.join(' / ') : '—';
}

function formatDate(v: string | null | undefined): string {
  return v ? new Date(v).toLocaleString() : '—';
}

/**
 * Reusable FM bundle detail body, loaded directly by bundle documentId from `/fm-bundles/:id`
 * (not the by-project list). Rendered by ProductDetailPage for the `fm_bundle` product type.
 */
export default function FmBundleDetail({ id }: { id: string }) {
  const location = useLocation();
  const dryRun = (location.state as { dryRun?: boolean } | null)?.dryRun === true;
  const [dryRunDismissed, setDryRunDismissed] = useState(false);
  const [bundle, setBundle] = useState<FmBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ type: 'success' | 'error' | 'info'; content: string } | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusDryRun, setStatusDryRun] = useState(false);

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    (async () => {
      try {
        const data = await getFmBundle(id);
        if (!data) throw new Error('FM bundle not found');
        setBundle(data);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const handleRefreshStatus = async () => {
    if (!id) return;
    setStatusLoading(true);
    setFlash(null);
    try {
      const result = await getFmBundleStatus(id);
      setBundle(prev => (prev ? { ...prev, currentStatus: result.currentStatus } : prev));
      if (result.dryRun) setStatusDryRun(true);
      // Re-fetch the full bundle so any ARNs backfilled by the status call (role/policy/AIP)
      // show up without a manual page reload. Failure here is non-fatal — the status flash
      // still reflects the refresh.
      try {
        const refreshed = await getFmBundle(id);
        if (refreshed) setBundle(refreshed);
      } catch {
        /* keep the status-updated bundle if the re-fetch fails */
      }
      setFlash({
        type: 'success',
        content: `Status: ${result.currentStatus}${result.ssmStatus ? ` (SSM: ${result.ssmStatus})` : ''}${result.inProgress ? ' — in progress' : ''}`,
      });
    } catch (e: any) {
      if (e instanceof MissingCredsError) {
        setFlash({ type: 'error', content: e.message });
      } else {
        setFlash({ type: 'error', content: e.message });
      }
    } finally {
      setStatusLoading(false);
    }
  };

  if (loading) return <Spinner size="large" />;
  if (error) return <Box color="text-status-error" fontSize="heading-m">Error: {error}</Box>;
  if (!bundle) return <Box color="text-body-secondary">FM bundle not found</Box>;

  const envName = bundle.environment?.name ?? '—';
  const profiles = bundle.fm_profiles ?? [];
  const computeRoles = bundle.computeRoles ?? [];
  const history = bundle.fm_provisioning_requests ?? [];

  // "Dry run" is indicated when the latest provisioning request's executionId is a dry-run
  // placeholder (prefixed "dry-run-"), or when a status refresh reported dryRun. Pick the latest
  // request by submittedAt (falling back to the first item when timestamps are absent).
  const latestRequest = history.length
    ? [...history].sort((a, b) =>
        (b.submittedAt ?? '').localeCompare(a.submittedAt ?? ''))[0]
    : null;
  const isDryRun = statusDryRun || !!latestRequest?.executionId?.startsWith('dry-run-');

  const kvItems = [
    { label: 'Project slug', value: bundle.projectSlug || '—' },
    { label: 'Service name', value: bundle.serviceName || '—' },
    {
      label: 'Environment',
      value: bundle.environment
        ? <Badge color={bundle.environment.isProduction ? 'red' : 'blue'}>{bundle.environment.name}</Badge>
        : '—',
    },
    { label: 'Team', value: bundle.team || '—' },
    {
      label: 'Status',
      value: (
        <SpaceBetween size="xs" direction="horizontal">
          <StatusIndicator type={fmStatusType(bundle.currentStatus)}>{bundle.currentStatus}</StatusIndicator>
          {isDryRun && <Badge color="grey">Dry run</Badge>}
        </SpaceBetween>
      ),
    },
    { label: 'Role ARN', value: bundle.roleArn || '—' },
    { label: 'Policy ARN', value: bundle.policyArn || '—' },
    { label: 'Budget', value: formatBudgetPeriod(bundle) },
    { label: 'Last synced', value: formatDate(bundle.lastSyncedAt) },
    { label: 'Tags', value: `ICLProject=${bundle.projectSlug} / ICLEnv=${envName} / ICLTeam=${bundle.team}` },
  ];

  // "How to use" is only meaningful once provisioning has succeeded and the role ARN was
  // backfilled. The first AIP ARN drives the examples (falls back to a placeholder if absent).
  const isActive = bundle.currentStatus === 'active';
  const roleArn = bundle.roleArn || '';
  const canShowUsage = isActive && !!roleArn;
  const firstAipArn = profiles.find(p => p.aipArn)?.aipArn || '<AIP_ARN>';

  const pythonSnippet = `import boto3
ROLE_ARN = "${roleArn}"
AIP_ARN = "${firstAipArn}"
creds = boto3.client("sts").assume_role(RoleArn=ROLE_ARN, RoleSessionName="app")["Credentials"]
br = boto3.client("bedrock-runtime", region_name="${FM_REGION}",
    aws_access_key_id=creds["AccessKeyId"], aws_secret_access_key=creds["SecretAccessKey"],
    aws_session_token=creds["SessionToken"])
resp = br.converse(modelId=AIP_ARN, messages=[{"role": "user", "content": [{"text": "Hello"}]}])
print(resp["output"]["message"]["content"][0]["text"])`;

  const cliSnippet = `aws sts assume-role --role-arn ${roleArn} --role-session-name app
aws bedrock-runtime converse --region ${FM_REGION} --model-id ${firstAipArn} --messages '[{"role":"user","content":[{"text":"Hello"}]}]'`;

  return (
    <SpaceBetween size="l">
      {dryRun && !dryRunDismissed && (
        <Flashbar
          items={[{
            type: 'info',
            content: 'Dry run: the SSM runbook was not executed.',
            dismissible: true,
            onDismiss: () => setDryRunDismissed(true),
          }]}
        />
      )}
      {flash && (
        <Flashbar items={[{ type: flash.type, content: flash.content, dismissible: true, onDismiss: () => setFlash(null) }]} />
      )}
      <Header
        variant="h1"
        actions={
          <Button onClick={handleRefreshStatus} loading={statusLoading} iconName="refresh">
            Refresh status
          </Button>
        }
      >
        {bundle.projectSlug} <Badge>FM Bundle</Badge>
      </Header>

      <KeyValuePairs items={kvItems} />

      <Container
        header={
          <Header variant="h2" description="How your application assumes the FM role and calls the models through their AIP ARNs.">
            How to use
          </Header>
        }
      >
        {!canShowUsage ? (
          <Box color="text-body-secondary">
            Available once provisioning succeeds. Click Refresh status.
          </Box>
        ) : (
          <SpaceBetween size="l">
            <KeyValuePairs
              columns={1}
              items={[
                {
                  label: 'FM role ARN',
                  value: (
                    <CopyToClipboard
                      variant="inline"
                      textToCopy={roleArn}
                      copyButtonAriaLabel="Copy FM role ARN"
                      copySuccessText="FM role ARN copied"
                      copyErrorText="Failed to copy FM role ARN"
                    />
                  ),
                },
                {
                  label: 'Region',
                  value: (
                    <CopyToClipboard
                      variant="inline"
                      textToCopy={FM_REGION}
                      copyButtonAriaLabel="Copy region"
                      copySuccessText="Region copied"
                      copyErrorText="Failed to copy region"
                    />
                  ),
                },
                {
                  label: 'Allowed compute roles',
                  value: computeRoles.length ? (
                    <SpaceBetween size="xxs">
                      {computeRoles.map((r, idx) => (
                        <div key={idx}>
                          <CopyToClipboard
                            variant="inline"
                            textToCopy={r}
                            copyButtonAriaLabel="Copy compute role ARN"
                            copySuccessText="Compute role copied"
                            copyErrorText="Failed to copy compute role"
                          />
                        </div>
                      ))}
                    </SpaceBetween>
                  ) : '—',
                },
              ]}
            />

            <Table
              variant="embedded"
              header={<Header counter={`(${profiles.length})`}>Models</Header>}
              items={profiles}
              columnDefinitions={[
                { id: 'shortName', header: 'Short name', cell: i => i.shortName || '—' },
                {
                  id: 'model',
                  header: 'Model',
                  cell: i => (i.provider ? `${i.modelId} (${i.provider})` : i.modelId || '—'),
                },
                { id: 'sourceProfile', header: 'Source profile', cell: i => i.inferenceProfileId || '—' },
                {
                  id: 'aipArn',
                  header: 'AIP ARN',
                  cell: i => i.aipArn
                    ? (
                      <CopyToClipboard
                        variant="inline"
                        textToCopy={i.aipArn}
                        copyButtonAriaLabel="Copy AIP ARN"
                        copySuccessText="AIP ARN copied"
                        copyErrorText="Failed to copy AIP ARN"
                      />
                    )
                    : '—',
                },
              ]}
              empty={<Box textAlign="center" color="text-body-secondary">No models</Box>}
            />

            <ExpandableSection
              headerText="Example (Python boto3)"
              headerActions={
                <CopyToClipboard
                  variant="icon"
                  textToCopy={pythonSnippet}
                  copyButtonAriaLabel="Copy Python example"
                  copySuccessText="Python example copied"
                  copyErrorText="Failed to copy Python example"
                />
              }
            >
              <Box variant="code">
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{pythonSnippet}</pre>
              </Box>
            </ExpandableSection>

            <ExpandableSection
              headerText="Example (AWS CLI)"
              headerActions={
                <CopyToClipboard
                  variant="icon"
                  textToCopy={cliSnippet}
                  copyButtonAriaLabel="Copy AWS CLI example"
                  copySuccessText="AWS CLI example copied"
                  copyErrorText="Failed to copy AWS CLI example"
                />
              }
            >
              <Box variant="code">
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{cliSnippet}</pre>
              </Box>
            </ExpandableSection>

            <Alert type="info" header="Access rules">
              <ul style={{ margin: 0, paddingInlineStart: '1.2em' }}>
                <li>Only the listed compute roles can assume the FM role.</li>
                <li>Call the models through the AIP ARNs; calling the model or system profile directly is denied.</li>
                <li>The application must run as (or assume) one of the compute roles.</li>
                <li>Compute roles in unsupported accounts, and SSO roles, are not granted assume permission by the runbook.</li>
              </ul>
            </Alert>
          </SpaceBetween>
        )}
      </Container>

      <Table
        variant="container"
        header={<Header counter={`(${profiles.length})`}>Models / AIPs</Header>}
        items={profiles}
        columnDefinitions={[
          { id: 'shortName', header: 'Short name', cell: i => i.shortName || '—' },
          { id: 'provider', header: 'Provider', cell: i => i.provider || '—' },
          { id: 'modelId', header: 'Model ID', cell: i => i.modelId || '—' },
          { id: 'aipArn', header: 'AIP ARN', cell: i => i.aipArn || '—' },
        ]}
        empty={<Box textAlign="center" color="text-body-secondary">No models</Box>}
      />

      <Table
        variant="container"
        header={<Header counter={`(${computeRoles.length})`}>Compute roles</Header>}
        items={computeRoles.map((r, idx) => ({ role: r, key: idx }))}
        trackBy="key"
        columnDefinitions={[
          { id: 'role', header: 'Role', cell: i => i.role },
        ]}
        empty={<Box textAlign="center" color="text-body-secondary">No compute roles</Box>}
      />

      <Table
        variant="container"
        header={<Header counter={`(${history.length})`}>Provisioning history</Header>}
        items={history}
        columnDefinitions={[
          { id: 'action', header: 'Action', cell: i => i.action || '—' },
          { id: 'status', header: 'Status', cell: i => <StatusIndicator type={fmStatusType(i.status)}>{i.status}</StatusIndicator> },
          { id: 'requestedBy', header: 'Requested by', cell: i => i.requestedBy || '—' },
          { id: 'submittedAt', header: 'Submitted at', cell: i => formatDate(i.submittedAt) },
          { id: 'completedAt', header: 'Completed at', cell: i => formatDate(i.completedAt) },
          { id: 'statusMessage', header: 'Message', cell: i => i.statusMessage || '—' },
        ]}
        empty={<Box textAlign="center" color="text-body-secondary">No provisioning history</Box>}
      />
    </SpaceBetween>
  );
}
